/**
 * Feature 024 FR-024-020, FR-024-022 to FR-024-024, D-2404, `specs/015-web-frontend/contracts/design-system.md`
 * § Token architecture § What a per-workspace palette replaces: the custom properties a served
 * palette rewrites on the document element, and nothing else. The subset is closed: the Team accent
 * (`--color-accent-bg`, `--color-accent-solid`, `--color-accent-on-solid` and `--color-accent-text`),
 * the accent as a line (`--color-accent-line`, the palette's focus ring) and the edge a solid fill
 * carries (`--color-accent-border`, the palette's outline, or transparent where it has none), the
 * focus ring and the selected tint. So the team colour reaches every use of action, current and
 * selected (2026-09-29, by the owner's report: "these ui elements.. should match the appearance
 * color of the team set... not just be purple all the time"): the filled buttons, the active tab
 * underline, the selected and open rows, the links, the focus ring and the focused field, the checked
 * controls, progress, the date picker's day, the info tone and the badges that mark what is current.
 * Nothing else moves: the neutral ramp, the page and sheet backgrounds, the eight named accents
 * (`--color-accent-blue-*` among them, which is the categorical and meaning blue and no longer the
 * Team's, 2026-09-29), the meaning colours, the categorical series and every chrome token,
 * `--color-workspace-mark` and the agent violet among them, are not here.
 *
 * `--color-text-on-solid` is not here either, and must not be (2026-09-18, after the owner found
 * black initials and black label text under a light accent): despite its name pairing with the blue
 * accent in the static tokens, it is the foreground every *other* solid chip reads, the label tag's
 * own colour, the status pill's state colour and the avatar's hashed tone. Writing the accent's
 * on-solid into it turns all of those to the accent's foreground, so an accent light enough to take
 * dark text blackens every label, pill and avatar in the product. Only
 * `--color-accent-on-solid` moves, which is what the filled button reads through
 * `--primary-foreground`.
 *
 * 2026-09-18, by the owner's decision: the tinted backgrounds the server serves (FR-024-021, the
 * bounded work-surface tint) are not applied. The owner asked for one colour that changes the
 * buttons and the active marks and leaves the pages as they are, so `--color-bg-app`,
 * `--color-bg-surface` and `--color-work-surface` stay the static tokens. `--color-bg-hover` stays
 * the neutral step of 2026-09-15 for the same reason. Both are recorded under T034 in 024's
 * `tasks.md`.
 */

/** The accent triple of one theme's applied ramp. */
export interface AppliedAccent {
  readonly bg: string;
  readonly solid: string;
  readonly text: string;
}

/** The three background tokens with the tint mixed in. */
export interface AppliedBackgrounds {
  readonly app: string;
  readonly surface: string;
  readonly work_surface: string;
}

/** One rendering of the applied palette, the shape of the API's `ThemeAppliedRamp`. */
export interface AppliedRamp {
  readonly accent: AppliedAccent;
  readonly backgrounds: AppliedBackgrounds;
  readonly focus_ring: string;
  readonly on_solid: string;
  /** The neutral edge every solid accent fill carries, null when the solid reaches 3:1 on every background by itself. */
  readonly outline: string | null;
}

/** The palette on a workspace read, the shape of the API's `ThemePalette` as far as application reads it. */
export interface AppliedPalette {
  readonly light: AppliedRamp;
  readonly dark: AppliedRamp;
}

/** The closed set of property names a palette may rewrite, in a fixed order so a test can compare output to it. */
export const APPLIED_PROPERTIES = [
  "--color-accent-bg",
  "--color-accent-solid",
  "--color-accent-text",
  "--color-accent-on-solid",
  "--color-accent-line",
  "--color-accent-border",
  "--color-focus-ring",
  "--color-bg-selected",
] as const;

export type AppliedProperty = (typeof APPLIED_PROPERTIES)[number];

/** The property values for the resolved theme. Every name of `APPLIED_PROPERTIES` is present and no other. */
export function appliedProperties(palette: AppliedPalette, theme: "light" | "dark"): Readonly<Record<AppliedProperty, string>> {
  const ramp = palette[theme];
  return {
    "--color-accent-bg": ramp.accent.bg,
    "--color-accent-solid": ramp.accent.solid,
    "--color-accent-text": ramp.accent.text,
    "--color-accent-on-solid": ramp.on_solid,
    "--color-accent-line": ramp.focus_ring,
    "--color-accent-border": ramp.outline ?? "transparent",
    "--color-focus-ring": ramp.focus_ring,
    "--color-bg-selected": ramp.accent.bg,
  };
}
